import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Shield,
  Server,
  GitBranch,
  User,
  Calendar,
  FileText,
  Loader2,
  Check,
  X,
} from "lucide-react";
import { approvalPublicApi, type ChecklistItem } from "../api/client";
import { titleCase } from "../lib/utils";

export default function ApprovePage() {
  const { token } = useParams<{ token: string }>();
  const queryClient = useQueryClient();
  const [decision, setDecision] = useState<"approved" | "rejected" | null>(
    null,
  );
  const [comments, setComments] = useState("");
  const [checklistResponses, setChecklistResponses] = useState<
    Record<string, boolean>
  >({});
  const [showConfirmation, setShowConfirmation] = useState(false);

  const {
    data: pageData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["approval-page", token],
    queryFn: () => approvalPublicApi.getPage(token!),
    enabled: !!token,
    retry: false,
  });

  const submitMutation = useMutation({
    mutationFn: (data: {
      decision: "approved" | "rejected";
      comments?: string;
      checklist_responses: Record<string, boolean>;
    }) => approvalPublicApi.submit(token!, data),
    onSuccess: () => {
      refetch();
      // Invalidate all related caches in case user is also logged in to the app
      queryClient.invalidateQueries({ queryKey: ["my-approvals"] });
      queryClient.invalidateQueries({ queryKey: ["deployments"] });
      queryClient.invalidateQueries({ queryKey: ["deployment"] });
      queryClient.invalidateQueries({ queryKey: ["deployment-requests"] });
      queryClient.invalidateQueries({ queryKey: ["deployment-request"] });
      queryClient.invalidateQueries({ queryKey: ["release-cycle"] });
      queryClient.invalidateQueries({ queryKey: ["release-cycles"] });
      queryClient.invalidateQueries({
        queryKey: ["release-cycle-deployments"],
      });
    },
  });

  const handleSubmit = () => {
    if (!decision) return;

    submitMutation.mutate({
      decision,
      comments: comments || undefined,
      checklist_responses: checklistResponses,
    });
    setShowConfirmation(false);
  };

  const handleChecklistChange = (itemId: string, checked: boolean) => {
    setChecklistResponses((prev) => ({
      ...prev,
      [itemId]: checked,
    }));
  };

  const allRequiredChecked = () => {
    if (!pageData?.checklist) return true;
    const requiredItems = pageData.checklist.filter((item) => item.required);
    return requiredItems.every((item) => checklistResponses[item.id]);
  };

  const canApprove = decision === "approved" ? allRequiredChecked() : true;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600 mx-auto" />
          <p className="mt-2 text-gray-600">Loading approval request...</p>
        </div>
      </div>
    );
  }

  if (error || !pageData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto" />
          <h1 className="mt-4 text-xl font-semibold text-gray-900">
            Invalid or Expired Link
          </h1>
          <p className="mt-2 text-gray-600">
            This approval link is invalid, has expired, or has already been
            used.
          </p>
        </div>
      </div>
    );
  }

  // Already decided
  if (pageData.already_decided) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          {pageData.previous_decision === "approved" ? (
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto" />
          ) : (
            <XCircle className="h-12 w-12 text-red-500 mx-auto" />
          )}
          <h1 className="mt-4 text-xl font-semibold text-gray-900">
            Already{" "}
            {pageData.previous_decision === "approved"
              ? "Approved"
              : "Rejected"}
          </h1>
          <p className="mt-2 text-gray-600">
            You have already submitted your decision for this approval request.
          </p>
          <div className="mt-6 p-4 bg-gray-50 rounded-lg text-left">
            <p className="text-sm text-gray-500">Service</p>
            <p className="font-medium">{titleCase(pageData.service_name)}</p>
            <p className="mt-2 text-sm text-gray-500">Environment</p>
            <p className="font-medium">{pageData.environment_name}</p>
            <p className="mt-2 text-sm text-gray-500">Version</p>
            <p className="font-medium font-mono">{pageData.version}</p>
          </div>
        </div>
      </div>
    );
  }

  // Expired
  if (pageData.is_expired) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <Clock className="h-12 w-12 text-yellow-500 mx-auto" />
          <h1 className="mt-4 text-xl font-semibold text-gray-900">
            Approval Request Expired
          </h1>
          <p className="mt-2 text-gray-600">
            This approval request has expired and can no longer be actioned.
          </p>
        </div>
      </div>
    );
  }

  // Submission successful
  if (submitMutation.isSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          {decision === "approved" ? (
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto" />
          ) : (
            <XCircle className="h-12 w-12 text-red-500 mx-auto" />
          )}
          <h1 className="mt-4 text-xl font-semibold text-gray-900">
            {decision === "approved" ? "Approved" : "Rejected"} Successfully
          </h1>
          <p className="mt-2 text-gray-600">
            Your decision has been recorded. Thank you for your response.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-indigo-600 px-6 py-4">
            <h1 className="text-xl font-semibold text-white">
              Deployment Approval Request
            </h1>
            <p className="text-indigo-200 text-sm mt-1">
              Your approval is required for the following deployment
            </p>
          </div>

          {/* Deployment Details */}
          <div className="p-6">
            <div className="grid grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <Server className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">Service</p>
                  <p className="font-medium text-gray-900">
                    {titleCase(pageData.service_name)}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Shield className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">Environment</p>
                  <p className="font-medium text-gray-900">
                    {pageData.environment_name}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <GitBranch className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">Version</p>
                  <p className="font-medium font-mono text-gray-900">
                    {pageData.version}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <User className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">Your Role</p>
                  <p className="font-medium text-gray-900 capitalize">
                    {pageData.approver_role}
                  </p>
                </div>
              </div>
            </div>

            {pageData.notes && (
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-start gap-3">
                  <FileText className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Notes</p>
                    <p className="text-gray-700 whitespace-pre-wrap">
                      {pageData.notes}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Custom Fields */}
            {pageData.custom_fields && pageData.custom_fields.length > 0 && (
              <div className="mt-6 border-t border-gray-200 pt-6">
                <h3 className="text-sm font-medium text-gray-900 mb-3">
                  Request Details
                </h3>
                <dl className="grid grid-cols-2 gap-4">
                  {pageData.custom_fields.map((field, index) => (
                    <div key={index}>
                      <dt className="text-sm text-gray-500">{field.name}</dt>
                      <dd className="font-medium text-gray-900">
                        {field.is_url ? (
                          <a
                            href={field.value}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-indigo-600 hover:text-indigo-500 hover:underline"
                          >
                            {field.value}
                          </a>
                        ) : (
                          field.value || "-"
                        )}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}

            {pageData.expires_at && (
              <div className="mt-4 flex items-center gap-2 text-sm text-yellow-600">
                <Calendar className="h-4 w-4" />
                <span>
                  Expires: {new Date(pageData.expires_at).toLocaleString()}
                </span>
              </div>
            )}
          </div>

          {/* Checklist */}
          {pageData.checklist && pageData.checklist.length > 0 && (
            <div className="border-t border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                Review Checklist
              </h2>
              <div className="space-y-3">
                {pageData.checklist.map((item: ChecklistItem) => (
                  <label
                    key={item.id}
                    className="flex items-start gap-3 cursor-pointer group"
                  >
                    <input
                      type="checkbox"
                      checked={checklistResponses[item.id] || false}
                      onChange={(e) =>
                        handleChecklistChange(item.id, e.target.checked)
                      }
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <div className="flex-1">
                      <span className="text-gray-700 group-hover:text-gray-900">
                        {item.text}
                        {item.required && (
                          <span className="text-red-500 ml-1">*</span>
                        )}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              <p className="mt-3 text-xs text-gray-500">
                * Required items must be checked before approving
              </p>
            </div>
          )}

          {/* Decision Section */}
          <div className="border-t border-gray-200 p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              Your Decision
            </h2>

            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setDecision("approved")}
                className={`flex items-center justify-center gap-2 p-4 rounded-lg border-2 transition-colors ${
                  decision === "approved"
                    ? "border-green-500 bg-green-50 text-green-700"
                    : "border-gray-200 hover:border-green-300 hover:bg-green-50"
                }`}
              >
                <Check className="h-5 w-5" />
                <span className="font-medium">Approve</span>
              </button>
              <button
                type="button"
                onClick={() => setDecision("rejected")}
                className={`flex items-center justify-center gap-2 p-4 rounded-lg border-2 transition-colors ${
                  decision === "rejected"
                    ? "border-red-500 bg-red-50 text-red-700"
                    : "border-gray-200 hover:border-red-300 hover:bg-red-50"
                }`}
              >
                <X className="h-5 w-5" />
                <span className="font-medium">Reject</span>
              </button>
            </div>

            <div className="mt-4">
              <label
                htmlFor="comments"
                className="block text-sm font-medium text-gray-700"
              >
                Comments{" "}
                {decision === "rejected" && (
                  <span className="text-gray-500">
                    (recommended for rejections)
                  </span>
                )}
              </label>
              <textarea
                id="comments"
                rows={3}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Add any comments about your decision..."
              />
            </div>

            {decision === "approved" && !allRequiredChecked() && (
              <div className="mt-4 flex items-center gap-2 text-sm text-yellow-600">
                <AlertTriangle className="h-4 w-4" />
                <span>
                  Please complete all required checklist items before approving
                </span>
              </div>
            )}

            <div className="mt-6">
              <button
                type="button"
                onClick={() => setShowConfirmation(true)}
                disabled={!decision || !canApprove || submitMutation.isPending}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-md text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                style={{
                  backgroundColor: !decision
                    ? "#9CA3AF"
                    : decision === "approved"
                      ? "#10B981"
                      : "#EF4444",
                }}
              >
                {submitMutation.isPending ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <span>Submit Decision</span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-sm text-gray-500">
          This is a secure approval link. Do not share this link with others.
        </p>
      </div>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900">
              Confirm {decision === "approved" ? "Approval" : "Rejection"}
            </h3>
            <p className="mt-2 text-gray-600">
              Are you sure you want to{" "}
              {decision === "approved" ? "approve" : "reject"} this deployment?
              This action cannot be undone.
            </p>
            <div className="mt-2 p-3 bg-gray-50 rounded text-sm">
              <p>
                <strong>Service:</strong> {titleCase(pageData.service_name)}
              </p>
              <p>
                <strong>Environment:</strong> {pageData.environment_name}
              </p>
              <p>
                <strong>Version:</strong> <code>{pageData.version}</code>
              </p>
            </div>
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => setShowConfirmation(false)}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className={`flex-1 py-2 px-4 rounded-md text-white font-medium ${
                  decision === "approved"
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-red-600 hover:bg-red-700"
                }`}
              >
                Confirm {decision === "approved" ? "Approval" : "Rejection"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
